// ./src/components/features/CreateEventWizard/wizardTypes.ts
//
// Shared shapes for the Create Event wizard: the in-progress draft, the step
// registry, and the context value. The draft is a PARTIAL of what will become an
// EventInput — the wizard fills it across steps and persists it to localStorage. The event is CREATED when the
// essentials are in (ESSENTIALS_LAST_STEP, ROADMAP #62 P6a); after that each step saves to the real event.

import type { OpResult } from '@sdflc/api-helpers';

import type { RecurrenceRule } from '@/utils/api';

import { EventCategoryGroup } from './data/eventTypeGroups';
import type { GiftRegistryLink, GiftSignupChoice } from './giftRegistry';
import type { CardPayments } from '@/components/features/CardPayments';

/** #77 — what "Limit tickets per order" starts at when the host turns it on in the wizard. */
export const DEFAULT_TICKETS_PER_ORDER = 10;

/** A chosen cover media item: a stored file and/or a resolvable URL. */
export interface WizardMediaRef {
  /** Set when the pick is a stored uploaded file. */
  uploadedFileId?: string | null;
  /** Always resolvable — the stored file's URL, or the external URL. */
  url: string;
}

/**
 * How the event is scheduled in time — mirrors the API's SCHEDULE_MODE boundary.
 *   single    — one start/end.
 *   recurring — the same event repeated (interchangeable occurrences).
 *   agenda    — one umbrella event over a span with heterogeneous sub-events.
 * For recurring/agenda the wizard captures the umbrella span here; the actual
 * sessions/schedule are edited after the event is created.
 */
export const SCHEDULE_MODE = {
  SINGLE: 'single',
  RECURRING: 'recurring',
  AGENDA: 'agenda',
} as const;

export type ScheduleMode = (typeof SCHEDULE_MODE)[keyof typeof SCHEDULE_MODE];

/** Where the event happens — mirrors the API's EVENT_FORMAT boundary. */
export const EVENT_FORMAT = {
  IN_PERSON: 'in_person',
  ONLINE: 'online',
  HYBRID: 'hybrid',
} as const;

export type EventFormat = (typeof EVENT_FORMAT)[keyof typeof EVENT_FORMAT];

/** How guests get in / RSVP — mirrors the API's EVENT_MODE boundary. */
export const EVENT_MODE = {
  LINK_RSVP: 'link_rsvp', // anyone with the link can RSVP
  MANAGED_INVITES: 'managed_invites', // only people you invite
  SAVE_THE_DATE: 'save_the_date', // announce now, RSVP later
  TICKETED: 'ticketed', // buy a ticket to attend
} as const;

export type EventMode = (typeof EVENT_MODE)[keyof typeof EVENT_MODE];

/**
 * Whether we capture guest identities or just a count. "headcount" is the
 * dead-simple (free-tier) path; "names" collects who's coming. On an RSVP event a
 * headcount reply is one lead guest with a party size; on a TICKETED one the buyer
 * is the only guest of record and the tickets are the count (#134 — the dashboard
 * reads them as such).
 */
export const GUEST_TRACKING = {
  NAMES: 'names',
  HEADCOUNT: 'headcount',
} as const;

export type GuestTracking = (typeof GUEST_TRACKING)[keyof typeof GUEST_TRACKING];

/**
 * #139 — how many people one HEADCOUNT reply may count when the host has not said. A headcount reply is one lead guest
 * with a party size, and the public form asks "How many in your party?" only when this is above 1 — so a headcount event
 * left at 1 (the plus-ones default) could never be told how many are coming, whatever its card promised.
 */
export const HEADCOUNT_PARTY_CAP = 10;

/** The most one reply may count on a headcount event: the host's own number when above 1, else `HEADCOUNT_PARTY_CAP`. */
export const headcountPartyCap = (rsvpGroupSize: number | null | undefined): number =>
  rsvpGroupSize != null && rsvpGroupSize > 1 ? rsvpGroupSize : HEADCOUNT_PARTY_CAP;

/**
 * Which QUICK path a draft is on: the headcount (#139 — an open RSVP that counts heads) or the ticket sale (#148 — a
 * ticketed event with one ticket type, the buyer plus the tickets as the count, #134).
 */
export const QUICK_KIND = {
  HEADCOUNT: 'headcount',
  TICKETS: 'tickets',
} as const;

export type QuickKind = (typeof QUICK_KIND)[keyof typeof QUICK_KIND];

/**
 * The quick path a draft is on, or null for the full wizard.
 * ⚠ A quick draft with no kind is a HEADCOUNT one: it was saved before #148, when that was the only quick path.
 */
export const quickKindOf = (draft: Pick<EventWizardDraft, 'quick' | 'quickKind'>): QuickKind | null => {
  if (!draft.quick) {
    return null;
  }

  return draft.quickKind === QUICK_KIND.TICKETS ? QUICK_KIND.TICKETS : QUICK_KIND.HEADCOUNT;
};

/**
 * #148 — the one ticket type the quick ticket sale makes on Finish, as the host wrote it. The "how many" is the event's
 * `capacity` (the places the dashboard and the checkout count), not a field of its own here.
 */
export interface QuickTicketDraft {
  name: string;
  /** 0 = free. Null = not filled in yet. */
  price: number | null;
  /** The EVENT's currency (a ticket is priced in it). Null = not chosen yet. */
  currencyCode: string | null;
}

/** The mutable event-in-progress the wizard builds up. Extended per step. */
export interface EventWizardDraft {
  // ── Step: Type ──────────────────────────────────────────────────────────
  /** Selected occasion (category) id — a seed UUID. */
  eventCategoryId: string | null;
  /** Its stable code (e.g. "birthday") — the key the assumptions engine reads. */
  eventCategoryCode: string | null;
  /** Its group (celebration / milestone / …). */
  eventCategoryGroup: EventCategoryGroup | null;

  // ── Step: Basics ────────────────────────────────────────────────────────
  title: string;
  subtitle: string;
  scheduleMode: ScheduleMode;
  /** Umbrella start (ISO-ish string from the date input) or null. */
  startsAt: string | null;
  endsAt: string | null;
  isAllDay: boolean;
  /** IANA timezone; defaults to the browser's. */
  timezone: string | null;
  /**
   * Which of the account's organizer pages lists the event (A2b #54b f). Asked only when the account has TWO OR MORE.
   * ⚠ THREE STATES, and they are different instructions to the server:
   *   undefined — not asked: the server decides (`EventCore.beforeCreate` attaches the account's ONLY live page);
   *   null      — asked, and the host chose none;
   *   an id     — that page.
   * So it is optional and never defaulted to null: sending null for an account with one page would un-list every event.
   */
  organizerProfileId?: string | null;

  // ── Step: Where ─────────────────────────────────────────────────────────
  eventFormat: EventFormat;
  /** Selected venue (place) id, or null. */
  venueId: string | null;
  /** Cached venue name for display without a re-fetch. */
  venueName: string | null;
  /** Cached venue address (one line) for the review summary. */
  venueAddress: string | null;
  /**
   * The venue id `capacity` was taken from, or null when the host typed it themselves.
   * ⚠ Without this there is no way to tell "500 because I picked the big hall" from "500 because I
   * meant 500", so changing venue would either clobber the host's number or never update a stale one.
   * See `utils/venueCapacity.ts`.
   */
  capacityFromVenueId: string | null;
  /** Online meeting link (for online / hybrid). */
  onlineMeetingUrl: string;

  // ── Step: Guests & RSVP ─────────────────────────────────────────────────
  /** How guests join — required choice (null until picked). */
  eventMode: EventMode | null;
  /** Names vs headcount (only asked for open-RSVP). */
  guestTracking: GuestTracking;
  /** Max people per RSVP (1 = no plus-ones). */
  rsvpGroupSize: number;
  /** #77 — ticketed: the most admission tickets one order may hold (null = no limit, "Limit tickets per order" off). */
  maxTicketsPerOrder: number | null;
  guestsNeedApproval: boolean;
  /** Attendee cap (null = unlimited). */
  capacity: number | null;
  enableWaitlist: boolean;
  /** RSVP cutoff (optional). */
  rsvpDeadline: string | null;
  /**
   * A2e #102 — a REPEATING event's cutoff instead: each date closes this many days before it (0 = its own day; null =
   * none). The schedule decides which of the two is in force; both are kept.
   */
  rsvpDeadlineDaysBefore: number | null;

  // ── Step: Media (polish) ────────────────────────────────────────────────
  /** Cover photo pick (shown in Review + on the event website). */
  coverImage: WizardMediaRef | null;
  /** Cover video pick (direct file / YouTube / Vimeo). */
  coverVideo: WizardMediaRef | null;
  /**
   * Highlight ids to attach ("Kid friendly", "Parking on site", …). These are NOT a
   * column on the event — they become event_highlight junction rows, so they're linked
   * after the event is created rather than being part of the create input.
   */
  highlightIds: string[];

  // ── Once CREATED (ROADMAP #62 P6a) ────────────────────────────────────────
  /**
   * The event this wizard created, or null before the essentials are in. ⚠ Persisted with the draft: a reload or a
   * phone resumed from the background must keep adding to THIS event, never create a second one.
   */
  createdEventId: string | null;
  /** The event features chosen (settings.features.on). Null until the event is created with its initial set. */
  featuresOn: string[] | null;
  /** highlightId → the event_highlight link id the wizard created, so un-ticking one really unlinks it. */
  highlightLinks: Record<string, string>;

  // ── Step: Basics — the organizer page's blurb (#62 P6(d2)) ───────────────
  /**
   * The event's Overview (event.summary): the short line the ORGANIZER PAGE shows with this event (owner, 2026-09-30),
   * edited on Basics beside the organizer page. Null / absent = untouched (nothing is sent).
   */
  summary?: string | null;

  // ── Step: Your event page (#62 P6(d)–(d3)) ────────────────────────────────
  /** The website blocks ticked on the step (keys from `websiteBlocks.ts`). Null = the defaults were never taken. */
  websiteBlocks?: string[] | null;
  /**
   * The event's DESCRIPTION — what the About block shows (d2: an ordinary rich-text block). Null = not read yet (a revisit
   * reads it back from the block `aboutWidgetId`). ⚠ All optional: a draft saved before these existed has none.
   */
  description?: string | null;
  /** The description's editor format (POLICY_CONTENT_FORMAT: richtext | markdown | html | plain). */
  descriptionFormat?: string | null;
  /** The host changed the description on the step since it was read — only then is an existing About block rewritten. */
  descriptionEdited?: boolean;
  /** The About block this wizard made (a text block carries no mark of its own). */
  aboutWidgetId?: string | null;
  /** The HOME page's order as the host arranged it (d3): `w:<widget id>` / `k:<block key>` items. Null = untouched. */
  websiteOrder?: string[] | null;

  // ── Step: Schedule (#62 P6, owner 2026-09-30) — a repeating event's rule ──
  /** The repeat rule as edited on the step. Null = not read from the event yet. ⚠ Optional: older drafts have none. */
  recurrence?: RecurrenceRule | null;
  /** The rule changed since it was last saved (the host's edit, or a re-seed) — Next saves it and re-creates the dates. */
  recurrenceEdited?: boolean;
  /**
   * The start / end / timezone the UNTOUCHED default rule was built from, or null once the rule is the host's (edited
   * here, or saved on the event). ⚠ When Basics' start moves, an untouched default is rebuilt from the new start
   * (review, 2026-09-30: Tuesdays at 19:00 were generated for an event moved to Thursday 18:00).
   */
  recurrenceSeed?: string | null;
  /**
   * REPEATING dates were made for this event (on leaving Schedule, or on Finish) and the host has not been back through
   * the Schedule step since the event stopped repeating. ⚠ While true a one-day / multi-day event still gets that step —
   * it is the one place that offers to remove the leftover dates (review, 2026-10-01: with Sessions now opt-in, a switch
   * from "Repeats" to "Multi-day" left dozens of weekly dates on the event, offered to guests as sessions).
   */
  repeatDatesMade?: boolean;

  // ── The quick headcount path (#139, owner 2026-10-08) ─────────────────────
  /**
   * A name, when, and a picture, then the event goes live with its link. Only `QUICK_STEPS` are shown; the event is an
   * open RSVP that counts heads (`quickDraftPatch`), CREATED and PUBLISHED on Finish (`quickEventInput`).
   * ⚠ Optional: a draft saved before #139 has none, and that is the full wizard.
   */
  quick?: boolean;
  /**
   * What taking the quick path overwrote (`before`) and what it wrote (`set`), so turning it off gives the host back their
   * full-setup answers — each field only while it still holds what the path set. Null when the path is off.
   */
  quickRestore?: { before: Partial<EventWizardDraft>; set: Partial<EventWizardDraft> } | null;
  /** #148 — which quick path (`QUICK_KIND`); read through `quickKindOf`. ⚠ Optional: a #139 draft has none (headcount). */
  quickKind?: QuickKind | null;
  /** #148 — the ticket the quick ticket sale makes on Finish. Null = not filled in yet. */
  quickTicket?: QuickTicketDraft | null;
  /**
   * #148 — the ticket type Finish made. ⚠ Persisted with the draft, like `createdEventId`: a Finish retried after a
   * refusal (or a reload) UPDATES that ticket, never makes a second one.
   */
  quickTicketTypeId?: string | null;

  // ── Step: Gifts (#140, owner 2026-10-09) — for the types where guests usually bring one ──
  /** The gift registries as the host wrote them (`giftRegistry.ts`). Null = untouched. ⚠ All optional: older drafts. */
  giftLinks?: GiftRegistryLink[] | null;
  /** The Link Buttons block the step made for them on the home page — rewritten (or removed) on a later visit, never doubled. */
  giftLinksWidgetId?: string | null;
  /** The links changed on the step since they were saved — only then is the block written. */
  giftLinksEdited?: boolean;
  /** Next was refused over a half-filled row — the step now marks what each row is missing. */
  giftLinksTried?: boolean;
  /** The Sign-up list is the event's GIFT list (its step and block say so); null = not chosen. */
  giftSignup?: GiftSignupChoice | null;
  /** The step switched the Sign-up list on or off since the features were saved — leaving it saves them. */
  giftFeatureEdited?: boolean;
}

/** Best-effort browser timezone (falls back to null). */
const detectTimezone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
};

/** A fresh, empty draft. */
export const emptyEventWizardDraft = (): EventWizardDraft => ({
  eventCategoryId: null,
  eventCategoryCode: null,
  eventCategoryGroup: null,
  title: '',
  subtitle: '',
  scheduleMode: SCHEDULE_MODE.SINGLE,
  startsAt: null,
  endsAt: null,
  isAllDay: false,
  timezone: detectTimezone(),
  eventFormat: EVENT_FORMAT.IN_PERSON,
  venueId: null,
  venueName: null,
  venueAddress: null,
  capacityFromVenueId: null,
  onlineMeetingUrl: '',
  eventMode: null,
  guestTracking: GUEST_TRACKING.NAMES,
  rsvpGroupSize: 1,
  maxTicketsPerOrder: null,
  guestsNeedApproval: false,
  capacity: null,
  enableWaitlist: false,
  rsvpDeadline: null,
  rsvpDeadlineDaysBefore: null,
  coverImage: null,
  coverVideo: null,
  highlightIds: [],
  createdEventId: null,
  featuresOn: null,
  highlightLinks: {},
  summary: null,
  websiteBlocks: null,
  description: null,
  descriptionFormat: null,
  descriptionEdited: false,
  aboutWidgetId: null,
  websiteOrder: null,
  recurrence: null,
  recurrenceEdited: false,
  recurrenceSeed: null,
  repeatDatesMade: false,
  quick: false,
  quickRestore: null,
  quickKind: null,
  quickTicket: null,
  quickTicketTypeId: null,
  giftLinks: null,
  giftLinksWidgetId: null,
  giftLinksEdited: false,
  giftLinksTried: false,
  giftSignup: null,
  giftFeatureEdited: false,
});

/**
 * Stable identifiers for the wizard's steps, in order.
 *
 * ⚠ The event is CREATED on leaving `guests` (ESSENTIALS_LAST_STEP) — everything before it is the essentials the owner
 * named (type, name, date/time, where, how guests join); everything after it writes to the real event, and from there
 * the host can finish at any step. There is no Review step: nothing after the essentials is pending any more.
 *
 * ⚠⚠ THE ORDER AFTER THE ESSENTIALS IS A DEPENDENCY ORDER, not a preference (owner, 2026-09-29):
 *   features → what the event uses; every SETUP step below is shown because of it.
 *   schedule → a repeating event's dates / an agenda's sessions (owner, 2026-09-30): right after the essentials' own
 *              "when", and BEFORE tickets — a ticket is bought for one of these dates.
 *   vouchers → BEFORE tickets: a ticket type's grant rule ("2 drink vouchers per ticket") names a voucher type, and
 *              an RSVP event's auto-grant rules do too — so the voucher types must exist first.
 *   tickets  → the ticket types, their price rules, and the vouchers each one grants.
 *   questions→ what guests are asked when they reply or buy — every event that takes replies.
 *   policies → what guests must AGREE to (#62 P6(e)): AFTER questions, because whether a waiver can ever be signed
 *              depends on them (a date of birth, an adult's email) — the step shows those gaps with the fix.
 *   invites  → WHO is invited (#62 P6c): after everything that shapes what an invitation asks and gives, before what
 *              guests DO — a Secret Santa or a Human Bingo is played among the people on this list.
 *   gifts    → for the types where guests usually bring one (#140): registry links, or the Sign-up list as a gift
 *              list — BEFORE sign-ups, because choosing the gift list switches that feature on and its step follows.
 *   signups / polls / games / draws → what guests do before and at the event (owner, 2026-09-29) — one step per
 *              feature; draws before the event page, which offers the "Prize winners" block.
 *   artists / pois / accommodations → what the event page SHOWS guests about the day and the trip (#62 P6(f)): the
 *              lineup, places nearby, where to stay.
 *   volunteers / partners → the people who help and who pay (#62 P6(f)). Artists and volunteers BEFORE budget: a
 *              booking's fee and paid shifts become budget lines ("Staff pay"), so the budget step shows them.
 *   timeline / tasks / budget → the host's and crew's own plan (#62 P6(e)). Guests see none of it, so it comes after
 *              everything guests do and before the steps that face outwards (the page, the channels, the media).
 *   website  → what the event PAGE shows (#62 P6(d)): after every step that makes something to show, so its blocks are
 *              offered with their content already there.
 *   channels → where it is promoted: each channel's tracked link is what the finish screen hands over to share.
 */
export const WIZARD_STEPS = [
  'type',
  'basics',
  'location',
  'guests',
  'features',
  'schedule',
  'vouchers',
  'tickets',
  'questions',
  'policies',
  'invites',
  'gifts',
  'signups',
  'polls',
  'games',
  'draws',
  'artists',
  'pois',
  'accommodations',
  'volunteers',
  'partners',
  'timeline',
  'tasks',
  'budget',
  'website',
  'channels',
  'polish',
] as const;

export type WizardStepKey = (typeof WIZARD_STEPS)[number];

/**
 * What the setup RULES say about this draft — the generated evaluator's answer for its mode, type and chosen features.
 * The shell computes it (it has the catalog); the registry only reads it, so the rules are never re-read here.
 */
export interface WizardSetupState {
  /** The features ON after the rules (chosen + required − unavailable). */
  on: readonly string[];
  /** Features the rules make unavailable, by code. */
  disabled: Readonly<Record<string, unknown>>;
  /**
   * #140 — what the event's TYPE says about gifts (the catalog's `gifts`, not a rule's answer): set when guests usually
   * bring one, with the type's gift ideas. Absent / null = not usual, and the Gifts step is not offered.
   */
  typeGifts?: { ideas?: string[] | null } | null;
}

/**
 * Does this draft get the Gifts step (#140)? When guests usually bring a gift to its TYPE — or when the step already put
 * something on the event (a registry block, a gift list), so a host who changed the type can still reach what they made.
 */
export const giftsNeedStep = (draft: EventWizardDraft, setup: WizardSetupState): boolean =>
  !!setup.typeGifts || !!draft.giftLinksWidgetId || !!draft.giftSignup;

export interface WizardStepMeta {
  key: WizardStepKey;
  /** i18n key for the short stepper label. */
  labelKey: string;
  /** Fallback label when the i18n key is absent. */
  labelDefault: string;
  /** Whether this step is fully implemented (vs a placeholder in the mockup). */
  implemented: boolean;
  /**
   * The event FEATURE this step sets up, if any (#62 — "when a host picks a feature, add a step where they set up what
   * it needs"). The Features step names these as "set up next".
   */
  feature?: string;
  /**
   * Shown only when this holds. Absent → always shown. ⚠ Only steps AFTER the essentials may be conditional: the
   * essentials are what every event needs.
   */
  when?: (draft: EventWizardDraft, setup: WizardSetupState) => boolean;
}

/**
 * Does an event of this mode get the guest-list step (#62 P6c)? Every mode people are INVITED to: an invite list, an
 * open RSVP (a host may still send personal invitations) and a save-the-date (it is sent to somebody). Not a ticketed
 * event — people buy their way in.
 * ⚠ Keyed on the MODE, like Tickets: the guest list is not a feature (it is always on), so the rules have no say in it.
 */
export const modeTakesGuestList = (mode: string | null | undefined): boolean => !!mode && mode !== EVENT_MODE.TICKETED;

/**
 * Does the WIZARD ask for the guest list (the Invite list step)? Only where it is the point: an invite-only event and a
 * save-the-date, which are SENT to people (owner, 2026-09-30: on an open RSVP or a ticketed event the step "can confuse
 * people" — they share one link; "maybe return it back later on").
 * ⓘ Narrower than `modeTakesGuestList`, which still says what the event CAN have — the finish screen keeps offering to
 *   send to guests an open-RSVP event already has.
 */
export const wizardAsksGuestList = (mode: string | null | undefined): boolean =>
  mode === EVENT_MODE.MANAGED_INVITES || mode === EVENT_MODE.SAVE_THE_DATE;

/**
 * Does this event get the wizard's Schedule step?
 *   - A REPEATING event always: its rule and its dates are made there, and a repeating event with no dates has nothing to
 *     reply to or buy.
 *   - A one-day or multi-day event only when the host chose the Sessions FEATURE (owner, 2026-10-01: "the sessions step …
 *     should act similar to features — user needs to select this as an option"; most events are one block of time, and
 *     every one of them was walked through it). Before that (2026-09-30) every mode got it, because a one-day networking
 *     event had nowhere to enter its schedule — the Sessions card is that place now, suggested for the types that use it.
 */
export const scheduleNeedsStep = (mode: string | null | undefined, sessionsOn: boolean, leftoverRepeatDates = false): boolean =>
  mode === SCHEDULE_MODE.RECURRING || ((mode === SCHEDULE_MODE.AGENDA || mode === SCHEDULE_MODE.SINGLE) && (sessionsOn || leftoverRepeatDates));

/** The ordered step registry driving the stepper + navigation. */
export const WIZARD_STEP_META: WizardStepMeta[] = [
  { key: 'type', labelKey: 'events:wizard.steps.type', labelDefault: 'Type', implemented: true },
  { key: 'basics', labelKey: 'events:wizard.steps.basics', labelDefault: 'Basics', implemented: true },
  { key: 'location', labelKey: 'events:wizard.steps.location', labelDefault: 'Where', implemented: true },
  { key: 'guests', labelKey: 'events:wizard.steps.guests', labelDefault: 'Guests', implemented: true },
  { key: 'features', labelKey: 'events:wizard.steps.features', labelDefault: 'Features', implemented: true },
  {
    key: 'schedule',
    labelKey: 'events:wizard.steps.schedule',
    labelDefault: 'Schedule',
    implemented: true,
    // The Sessions feature — or a REPEATING schedule mode, whose dates are made here whatever the features say.
    feature: 'sessions',
    // ⓘ …and an event that STOPPED repeating with its dates still on it, until the host has been through the step.
    when: (draft, setup) => scheduleNeedsStep(draft.scheduleMode, setup.on.includes('sessions'), !!draft.repeatDatesMade),
  },
  {
    key: 'vouchers',
    labelKey: 'events:wizard.steps.vouchers',
    labelDefault: 'Vouchers',
    implemented: true,
    feature: 'vouchers',
    when: (_draft, setup) => setup.on.includes('vouchers'),
  },
  {
    key: 'tickets',
    labelKey: 'events:wizard.steps.tickets',
    labelDefault: 'Tickets',
    implemented: true,
    feature: 'tickets',
    // ⚠ The MODE, not the rules' answer: a ticketed event must never lose the step where it creates what it sells
    // because the catalog (and so the rules) failed to load.
    when: (draft) => draft.eventMode === EVENT_MODE.TICKETED,
  },
  {
    key: 'questions',
    labelKey: 'events:wizard.steps.questions',
    labelDefault: 'Questions',
    implemented: true,
    feature: 'questions',
    // Every event that takes replies (owner) — i.e. unless the rules make questions unavailable (a save-the-date).
    when: (_draft, setup) => !setup.disabled.questions,
  },
  {
    key: 'policies',
    labelKey: 'events:wizard.steps.policies',
    labelDefault: 'Policies',
    implemented: true,
    feature: 'policies',
    when: (_draft, setup) => setup.on.includes('policies'),
  },
  {
    key: 'invites',
    labelKey: 'events:wizard.steps.invites',
    labelDefault: 'Invite list',
    implemented: true,
    // No `feature`: the guest list is always on. The MODE decides (see `wizardAsksGuestList`).
    when: (draft) => wizardAsksGuestList(draft.eventMode),
  },
  {
    key: 'gifts',
    labelKey: 'events:wizard.steps.gifts',
    labelDefault: 'Gifts',
    implemented: true,
    // No `feature`: gifts are not a feature — the TYPE decides (see `giftsNeedStep`).
    when: (draft, setup) => giftsNeedStep(draft, setup),
  },
  {
    key: 'signups',
    labelKey: 'events:wizard.steps.signups',
    labelDefault: 'Sign-up list',
    implemented: true,
    feature: 'signup_list',
    when: (_draft, setup) => setup.on.includes('signup_list'),
  },
  {
    key: 'polls',
    labelKey: 'events:wizard.steps.polls',
    labelDefault: 'Polls & quizzes',
    implemented: true,
    feature: 'polls',
    when: (_draft, setup) => setup.on.includes('polls'),
  },
  {
    key: 'games',
    labelKey: 'events:wizard.steps.games',
    labelDefault: 'Games',
    implemented: true,
    feature: 'party_games',
    when: (_draft, setup) => setup.on.includes('party_games'),
  },
  // #62 P6(e) — one step per remaining feature, same pattern.
  {
    key: 'draws',
    labelKey: 'events:wizard.steps.draws',
    labelDefault: 'Prize draws',
    implemented: true,
    feature: 'prize_draws',
    when: (_draft, setup) => setup.on.includes('prize_draws'),
  },
  // #62 P6(f) — the rest of what a type offers, same pattern. ⓘ No step (yet) for the event map, race timing, tallies,
  // finance, rentals, travel, files, notes or inventory: each needs a page-sized editor, or only matters at or after the
  // event — their pages are one tap away in the menu.
  {
    key: 'artists',
    labelKey: 'events:wizard.steps.artists',
    labelDefault: 'Artists & speakers',
    implemented: true,
    feature: 'artists',
    when: (_draft, setup) => setup.on.includes('artists'),
  },
  {
    key: 'pois',
    labelKey: 'events:wizard.steps.pois',
    labelDefault: 'Places nearby',
    implemented: true,
    feature: 'pois',
    when: (_draft, setup) => setup.on.includes('pois'),
  },
  {
    key: 'accommodations',
    labelKey: 'events:wizard.steps.accommodations',
    labelDefault: 'Accommodations',
    implemented: true,
    feature: 'accommodations',
    when: (_draft, setup) => setup.on.includes('accommodations'),
  },
  {
    key: 'volunteers',
    labelKey: 'events:wizard.steps.volunteers',
    labelDefault: 'Volunteers & staff',
    implemented: true,
    feature: 'volunteers',
    when: (_draft, setup) => setup.on.includes('volunteers'),
  },
  {
    key: 'partners',
    labelKey: 'events:wizard.steps.partners',
    labelDefault: 'Partners & sponsors',
    implemented: true,
    feature: 'partners',
    when: (_draft, setup) => setup.on.includes('partners'),
  },
  {
    key: 'timeline',
    labelKey: 'events:wizard.steps.timeline',
    labelDefault: 'Timeline',
    implemented: true,
    feature: 'timeline',
    when: (_draft, setup) => setup.on.includes('timeline'),
  },
  {
    key: 'tasks',
    labelKey: 'events:wizard.steps.tasks',
    labelDefault: 'Tasks',
    implemented: true,
    feature: 'tasks',
    when: (_draft, setup) => setup.on.includes('tasks'),
  },
  {
    key: 'budget',
    labelKey: 'events:wizard.steps.budget',
    labelDefault: 'Budget',
    implemented: true,
    feature: 'budget',
    when: (_draft, setup) => setup.on.includes('budget'),
  },
  // #62 P6(d) — always: every event has a page, and a page that shows only the hero shows nothing the wizard made.
  { key: 'website', labelKey: 'events:wizard.steps.website', labelDefault: 'Event page', implemented: true },
  {
    key: 'channels',
    labelKey: 'events:wizard.steps.channels',
    labelDefault: 'Marketing channels',
    implemented: true,
    feature: 'marketing_channels',
    when: (_draft, setup) => setup.on.includes('marketing_channels'),
  },
  { key: 'polish', labelKey: 'events:wizard.steps.polish', labelDefault: 'Media', implemented: true },
];

/**
 * #139 — the steps of the QUICK headcount path: the type (where it is chosen), the name and when, and the picture. Every
 * other step is the full wizard's — the quick path reuses these three rather than being a second creation flow.
 * ⚠ No Where and no Guests: the event is made with no place (added later from its dashboard) and as an open RSVP that
 *   counts heads, so nothing on them is asked.
 */
export const QUICK_STEPS: readonly WizardStepKey[] = ['type', 'basics', 'polish'];

/**
 * #148 — the steps of the QUICK ticket sale: the type, the name and when, WHERE (a paid event needs its place: the
 * receipt and the tax), the ticket (its price, and how many), and the picture. All the full wizard's own steps — the
 * `tickets` key shows the quick ticket screen on this path (the full Tickets step needs the event to exist already).
 * ⚠ No Guests: the event is ticketed and counts the buyer plus the tickets (#134), so nothing on it is asked.
 */
export const QUICK_TICKET_STEPS: readonly WizardStepKey[] = ['type', 'basics', 'location', 'tickets', 'polish'];

/** The steps of the quick path a draft is on. */
export const quickStepsOf = (draft: Pick<EventWizardDraft, 'quick' | 'quickKind'>): readonly WizardStepKey[] =>
  quickKindOf(draft) === QUICK_KIND.TICKETS ? QUICK_TICKET_STEPS : QUICK_STEPS;

/** The features that get their own setup step, by code. */
export const FEATURE_SETUP_STEP: Readonly<Record<string, WizardStepKey>> = Object.fromEntries(
  WIZARD_STEP_META.filter((meta) => meta.feature).map((meta) => [meta.feature as string, meta.key]),
);

/** The steps this draft goes through, in order. */
export const visibleWizardSteps = (draft: EventWizardDraft, setup: WizardSetupState): WizardStepMeta[] =>
  draft.quick
    ? WIZARD_STEP_META.filter((meta) => quickStepsOf(draft).includes(meta.key))
    : WIZARD_STEP_META.filter((meta) => !meta.when || meta.when(draft, setup));

/**
 * The step to SHOW for `key`: itself when visible, else the next visible step after it (the last one if none).
 *
 * ⚠⚠ THE WIZARD NAVIGATES BY KEY, NEVER BY INDEX. Steps come and go with the draft — ticking Vouchers on the Features
 * step adds a step AFTER it, switching to "Sell tickets" adds one — so a stored index would silently point at a
 * different screen. A restored draft whose step no longer applies lands on what follows.
 */
export const resolveWizardStep = (steps: readonly WizardStepMeta[], key: WizardStepKey): WizardStepMeta => {
  const own = steps.find((meta) => meta.key === key);

  if (own) {
    return own;
  }

  const order = WIZARD_STEPS.indexOf(key);

  return steps.find((meta) => WIZARD_STEPS.indexOf(meta.key) > order) ?? steps[steps.length - 1];
};

/** The visible step after / before `key`, or null at either end. */
export const adjacentWizardStep = (
  steps: readonly WizardStepMeta[],
  key: WizardStepKey,
  direction: 1 | -1,
): WizardStepKey | null => {
  const index = steps.indexOf(resolveWizardStep(steps, key));

  return steps[index + direction]?.key ?? null;
};

/** The last essentials step — leaving it creates the event (or re-saves the essentials once it exists). */
export const ESSENTIALS_LAST_STEP: WizardStepKey = 'guests';

/** The value exposed through WizardContext to every step. */
export interface EventWizardContextValue {
  draft: EventWizardDraft;
  /**
   * Shallow-merge a patch into the draft (persists to localStorage). #148 — or an updater of the LATEST draft, for a patch
   * built from a nested value (`quickTicket`): built from the render's copy, two patches before a render undo each other.
   */
  patchDraft: (patch: Partial<EventWizardDraft> | ((prev: EventWizardDraft) => Partial<EventWizardDraft>)) => void;
  /** Index of the current step in WIZARD_STEP_META. */
  stepIndex: number;
  stepKey: WizardStepKey;
  /** True once the current step's minimum requirement is met (gates Next). */
  canAdvance: boolean;
  goNext: () => void;
  goBack: () => void;
  goToStep: (key: WizardStepKey) => void;
  /** True when the draft holds anything worth confirming-before-leaving. */
  isDirty: boolean;
  /** The created event's id — null until the essentials are saved (#62 P6a). */
  createdEventId: string | null;
  /** A save to the real event is in flight — steps disable their inputs. */
  saving: boolean;
  /** Switch the guest mode of the CREATED event (the Features step's "Sell tickets instead"). */
  switchEventMode: (eventMode: EventMode) => Promise<void>;
  /**
   * Run a step's OWN write as the wizard's save: `saving` is on for its length, so Next / Finish / Back cannot start a
   * second write of the same thing meanwhile (review, 2026-09-30: "Create the dates now" + Next made every date twice).
   */
  runExclusive: <T>(work: () => Promise<T>) => Promise<T>;
  /**
   * The last refused save to the event, or null — the shell shows it in the sticky footer, beside the button that sent it
   * (owner, 2026-10-22); a step also draws the error of a field it owns under that input (A2e #94: the deadline, on Guests).
   */
  saveError: OpResult | null;
  /**
   * #148 — can this account take card payments? Checked once the draft is on the quick ticket sale, and shared by its
   * screens (the ticket, the picture's "when you finish") so they say the same; Finish asks again before publishing.
   */
  cardPayments: CardPayments;
}

/** The key a default repeat rule is seeded from — its start, end and timezone. */
export const recurrenceSeedKey = (event: { startsAt?: string | null; endsAt?: string | null; timezone?: string | null }): string =>
  [event.startsAt ?? '', event.endsAt ?? '', event.timezone ?? ''].join('|');
